import { LoadingButton } from '@mui/lab';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Collapse,
  LinearProgress,
  Typography,
} from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers';
import { addDays, addWeeks, eachDayOfInterval, format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { withDataPoint } from '../../../../diagnostics/client';
import { CSDisclaimerTest } from '../../../../dto/events';
import {
  Provider,
  useBookAppointmentMutation,
  useGetAvailableTimeslotsForProviderQuery,
} from '../../../../generated/graphql';
import {
  trackAdditionalMatchSelectedOrBooked,
  trackSchedulingTimeslotsAvailableWhenBooking,
} from '../../../../helpers/analytics';
import { formatProviderNameWithCredential } from '../../../../helpers/format-helpers';
import { SOONER_START_DATE } from '../../../../helpers/matching';
import { useAppDispatch } from '../../../../pages/assessment';
import {
  selectPatient,
  selectReasonVisitAnswers,
} from '../../../../redux/assessment';
import { selectLdFlags } from '../../../../redux/ld-flags';
import { selectMatching } from '../../../../redux/matching';
import { updateMeta } from '../../../../redux/meta';
import { canProviderPrescribeCSInState } from '../../../../shared/licenses-to-eligible-states';
import TimePicker from './time-picker';

export interface Range {
  start: Date;
  end: Date;
}

export const DEFAULT_AVAILABILITY_WINDOW_IN_WEEKS = 4;

const today = new Date();
const fourWeeksFromToday = addWeeks(
  today,
  DEFAULT_AVAILABILITY_WINDOW_IN_WEEKS
);
const startTimeFromToday = addDays(today, SOONER_START_DATE);

interface Props {
  provider: Provider;
  selectProvider: (appointmentTime: string) => Promise<{ status: string }>;
  confirmingAppointment: boolean;
  setConfirmingAppointment: (isConfirming: boolean) => void;
  wasProviderAdditionalMatch?: boolean;
}

const timeToMapKey = (date: Date, amPm?: string) =>
  `${format(date, 'M-d')}-${amPm || (date.getHours() >= 12 ? 'PM' : 'AM')}`;

const Schedule = ({
  provider,
  selectProvider,
  confirmingAppointment,
  setConfirmingAppointment,
  wasProviderAdditionalMatch,
}: Props) => {
  const dispatch = useAppDispatch();
  const { matches } = useSelector(selectMatching);
  const patient = useSelector(selectPatient);
  const reasonVisitAnswers = useSelector(selectReasonVisitAnswers);

  const { data, loading, refetch } = useGetAvailableTimeslotsForProviderQuery({
    variables: {
      checkForNpDailyMax: false,
      endTime: fourWeeksFromToday,
      providerId: provider.id,
      startTime: today,
      timeslotDurationMinutes: 60,
      timeslotVisitType: 'TELE NP',
    },
  });

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showTimePicker, setShowTimePicker] = useState<boolean>(false);

  const [selectedStartTime, setSelectedStartTime] = useState<string>();
  const [selectedEndTime, setSelectedEndTime] = useState<string>();

  const refreshScheduleAvailabilityMap = () =>
    data?.getAvailableTimeslotsForProvider.reduce<Map<string, Range[]>>(
      (map, currentSlot) => {
        if (!currentSlot.isAvailable) return map;

        const startTime = new Date(currentSlot.startTime);
        const newMap = { ...map };
        if (!newMap[timeToMapKey(startTime)]) {
          newMap[timeToMapKey(startTime)] = [];
        }
        newMap[timeToMapKey(startTime)].push({
          start: startTime,
          end: new Date(currentSlot.endTime),
        } as Range);

        return newMap;
      },
      new Map()
    );

  const [scheduleAvailabilityMap, setScheduleAvailabilityMap] = useState<
    Map<string, Range[]>
  >(refreshScheduleAvailabilityMap());
  const [unavailableDays, setUnavailableDays] = useState<Date[]>();
  const { addToast } = useToasts();

  const refreshUnavailableDays = (): Date[] => {
    const unavailableDaysTemp = [];
    if (data) {
      eachDayOfInterval({
        start: startTimeFromToday,
        end: fourWeeksFromToday,
      }).forEach(day => {
        if (
          !scheduleAvailabilityMap[timeToMapKey(day, 'AM')] &&
          !scheduleAvailabilityMap[timeToMapKey(day, 'PM')]
        ) {
          unavailableDaysTemp.push(day);
        }
      });
    }
    return unavailableDaysTemp;
  };

  useEffect(() => {
    if (scheduleAvailabilityMap) setUnavailableDays(refreshUnavailableDays());
  }, [scheduleAvailabilityMap]);

  useEffect(() => {
    if (data) setScheduleAvailabilityMap(refreshScheduleAvailabilityMap());
  }, [data]);

  useEffect(() => {
    if (selectedDate) {
      setShowTimePicker(true);
      setSelectedStartTime('');
    }
  }, [selectedDate]);

  const [bookAppointmentMutation] = useBookAppointmentMutation();

  const refetchAvailability = async () => {
    setSelectedDate(null);
    setSelectedStartTime('');
    void refetch();
    setScheduleAvailabilityMap(refreshScheduleAvailabilityMap());
  };

  const { shouldShowControlledSubstanceDisclaimer } =
    useSelector(selectLdFlags);

  const getMatchCSDisclaimerMetrics = (): CSDisclaimerTest => {
    const { RV_MainReason, RV_SecondaryReason, RV_SpecialtyCare } =
      reasonVisitAnswers;

    const choseCSProvider = canProviderPrescribeCSInState(
      provider.licenses,
      patient.addressState
    );

    const numberOfMatchesWithCSLicense = matches.filter(match => {
      const canPrescribeCSInState = canProviderPrescribeCSInState(
        match.licenses,
        patient.addressState
      );

      return canPrescribeCSInState;
    }).length;

    return {
      should_show_controlled_substance_disclaimer:
        shouldShowControlledSubstanceDisclaimer,
      rv_main_reason: RV_MainReason,
      rv_secondary_reason: RV_SecondaryReason,
      rv_specialty_care: RV_SpecialtyCare,
      number_of_matches: matches.length,
      number_of_matches_with_cs_license: numberOfMatchesWithCSLicense,
      chose_no_match: false,
      chose_cs_provider: choseCSProvider,
    };
  };

  const confirmAppointment = async () => {
    await withDataPoint('book_appointment', async dp => {
      if (wasProviderAdditionalMatch)
        trackAdditionalMatchSelectedOrBooked(provider.id, 'booked');

      setConfirmingAppointment(true);
      addToast('Booking your appointment', { appearance: 'info' });

      const { status: selectProviderStatus } = await selectProvider(
        selectedStartTime
      );
      const CSDisclaimerMetrics = getMatchCSDisclaimerMetrics();

      Object.entries(CSDisclaimerMetrics).forEach(([k, v]) => {
        dp[k] = v;
      });

      dp.provider_id = provider.id;
      dp.timeslot = {
        startTime: new Date(selectedStartTime).getTime(),
        endTime: new Date(selectedEndTime).getTime(),
        timezone: 'UTC',
      };
      dp.was_provider_additional_match = wasProviderAdditionalMatch;

      let res;
      try {
        res = await bookAppointmentMutation({
          variables: {
            input: {
              providerNpi: provider.npi,
              providerId: provider.id,
              startTime: selectedStartTime,
              endTime: selectedEndTime,
              wasProviderAdditionalMatch,
            },
          },
        });
      } catch (_e) {
        // Do nothing.
      } finally {
        setConfirmingAppointment(false);
      }

      if (
        selectProviderStatus === 'success' &&
        res?.data?.bookAppointment?.ehrEncounterId
      ) {
        dp.user_success = true;
        trackSchedulingTimeslotsAvailableWhenBooking(
          data.getAvailableTimeslotsForProvider.map(slot => slot.startTime),
          provider.id,
          selectedStartTime
        );

        dispatch(updateMeta());
      } else {
        await refetchAvailability();

        addToast('Sorry, something went wrong. Please try again.', {
          appearance: 'error',
        });
      }
    });
  };

  const isDayDisabled = (day: Date): boolean =>
    !!unavailableDays?.find(date => date.toDateString() === day.toDateString());

  return (
    <Card variant="outlined">
      {confirmingAppointment && <LinearProgress />}

      <CardHeader
        avatar={<Avatar src={provider.photo} />}
        sx={{ pb: 0 }}
        title="Book a virtual appointment"
        subheader={formatProviderNameWithCredential(
          provider.firstName,
          provider.lastName,
          provider.primaryCredential
        )}
      />

      <CardContent sx={{ pt: 0 }}>
        <Typography variant="h6" sx={{ mt: 3 }}>
          {selectedDate ? format(selectedDate, 'EEEE, MMMM d') : 'Pick a Day'}
        </Typography>

        <Box id="scheduling-date-picker">
          <DateCalendar
            value={selectedDate}
            onChange={setSelectedDate}
            shouldDisableDate={isDayDisabled}
            views={['day']}
            loading={loading}
            maxDate={fourWeeksFromToday}
            minDate={startTimeFromToday}
            disablePast
            renderLoading={() => <CircularProgress />}
            sx={{ width: '100%' }}
          />
        </Box>

        <Collapse in={showTimePicker} unmountOnExit>
          {selectedDate && scheduleAvailabilityMap && (
            <TimePicker
              loading={loading}
              disabled={confirmingAppointment}
              morningTimeSlots={
                scheduleAvailabilityMap[timeToMapKey(selectedDate, 'AM')]
              }
              afternoonTimeSlots={
                scheduleAvailabilityMap[timeToMapKey(selectedDate, 'PM')]
              }
              selectedStartTime={selectedStartTime}
              setSelectedStartTime={setSelectedStartTime}
              setSelectedEndTime={setSelectedEndTime}
            />
          )}
        </Collapse>

        <LoadingButton
          variant="contained"
          size="large"
          fullWidth
          disabled={!selectedStartTime || confirmingAppointment}
          onClick={confirmAppointment}
          loading={confirmingAppointment}
          sx={{ mt: 2 }}
        >
          Confirm Appointment
        </LoadingButton>
      </CardContent>
    </Card>
  );
};

export default Schedule;
